import React from 'react';
import cx from 'classnames';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  useUpdateCardCoverMutation,
  SelectCoverQuery,
} from '@trello/graphql/generated';
import {
  smallestPreviewBiggerThan,
  biggestPreview,
} from 'app/common/lib/util/preview';

import styles from './SelectTextColor.less';

const format = forTemplate('card_cover_chooser');

type Cover = NonNullable<SelectCoverQuery['card']>['cover'];

interface SelectTextColorProps {
  cardId: string;
  cardName?: string;
  cover: Cover;
}

const darkFullCoverGradient =
  'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))';
const lightFullCoverGradient =
  'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))';

export const SelectTextColor: React.FunctionComponent<SelectTextColorProps> = ({
  cardId,
  cardName,
  cover,
}) => {
  const [updateCardCover] = useUpdateCardCoverMutation();
  const setBrightness = async (brightness: 'light' | 'dark') => {
    await updateCardCover({
      variables: {
        cardId,
        cover: {
          brightness,
        },
      },
    });
  };

  let textColor = 'dark';
  if (cover?.brightness === 'dark') {
    textColor = 'light';
  }
  const coverColor = cover?.color;
  const colorCoverClassName = coverColor ? styles[coverColor] : null;
  const preview =
    cover && cover.scaled
      ? smallestPreviewBiggerThan(cover.scaled, 86, 64) ||
        biggestPreview(cover.scaled)
      : null;

  const tileClassName = cx(styles.textColorTile, colorCoverClassName, {
    [styles.colorCardCover]: colorCoverClassName,
  });

  const darkTileStyle = {
    backgroundImage: preview?.url
      ? `${darkFullCoverGradient}, url(${preview.url})`
      : undefined,
  };

  const lightTileStyle = {
    backgroundImage: preview?.url
      ? `${lightFullCoverGradient}, url(${preview.url})`
      : undefined,
  };

  return (
    <div className={styles.selectTextColor}>
      <h4 className={styles.heading}>{format('text-color')}</h4>
      <div className={styles.textColorTiles}>
        <div
          role="button"
          onClick={() => setBrightness('dark')}
          className={cx(tileClassName, {
            [styles.selected]: textColor === 'light',
          })}
          style={darkTileStyle}
        >
          <h3 className={cx(styles.name, styles.light)}>{cardName}</h3>
        </div>
        <div
          role="button"
          onClick={() => setBrightness('light')}
          className={cx(tileClassName, {
            [styles.selected]: textColor === 'dark',
          })}
          style={lightTileStyle}
        >
          <h3 className={styles.name}>{cardName}</h3>
        </div>
      </div>
    </div>
  );
};
